/**
 * Toast utility functions for consistent error handling across the application
 */

import { extractErrorMessage } from './error-utils';
import { logger } from '@/lib/logger';

/**
 * Shows an error toast with safe error message extraction.
 * Includes defensive programming to handle toast failures gracefully.
 *
 * @param toast - The toast function from useToast hook
 * @param error - The error value (can be Error, object, string, etc.)
 * @param defaultMessage - Default message to show if error extraction fails
 *
 * @example
 * ```typescript
 * import { useToast } from '@/hooks/use-toast';
 * import { showErrorToast } from '@/lib/utils/toast-utils';
 *
 * const { toast } = useToast();
 *
 * try {
 *   await api.someMethod();
 * } catch (error) {
 *   showErrorToast(toast, error, 'Failed to perform action');
 * }
 * ```
 */
export function showErrorToast(
  toast: (options: {
    title: string;
    description: string;
    variant?: 'destructive' | 'default';
  }) => void,
  error: unknown,
  defaultMessage: string
): void {
  try {
    const errorMessage = extractErrorMessage(error) || defaultMessage;
    toast({
      title: 'Error',
      description: errorMessage,
      variant: 'destructive',
    });
  } catch (toastError) {
    // Fallback if toast itself fails (defensive programming)
    // Use logger here since toast failed - logger should still work
    logger.error('Failed to show error toast', {
      defaultMessage,
      toastError: toastError instanceof Error ? toastError.message : String(toastError),
    });
  }
}
